import { z } from 'zod'

// Communications, Phase 3 (ADR 0036; docs/architecture/BRE-Communications-Plan.md
// §5.2, §6, §8, §11): text messages. Shared by Platform Core (which decides,
// renders and counts), the web Worker (which talks to Twilio and checks its
// signatures) and the staff UI (which shows the same count and cost live).
// Nothing here sends anything.

// ------------------------------------------------------------- keywords

// What a person's reply means. Twilio's own opt-out keywords (its help
// center, read 2026-10-09: STOP, STOPALL, UNSUBSCRIBE, CANCEL, END, QUIT),
// plus the words the FCC's 2024 revocation order and CTIA treat as a
// reasonable way to stop (OPTOUT, OPT OUT, REVOKE, STOP ALL). Hub honors the
// longer list even if Twilio doesn't block on every word: a stop Hub reads
// that Twilio didn't only means Hub sends less.
export const SMS_STOP_KEYWORDS = ['STOP', 'STOPALL', 'STOP ALL', 'UNSUBSCRIBE', 'CANCEL', 'END', 'QUIT', 'OPTOUT', 'OPT OUT', 'REVOKE'] as const
// Twilio's defaults (START, YES, UNSTOP; HELP, INFO), from memory of its
// Advanced Opt-Out page, not re-read here: unverified.
export const SMS_START_KEYWORDS = ['START', 'YES', 'UNSTOP'] as const
export const SMS_HELP_KEYWORDS = ['HELP', 'INFO'] as const

export type SmsKeywordClass = 'stop' | 'start' | 'help' | 'other'
export const SMS_KEYWORD_LABELS: Record<SmsKeywordClass, string> = { stop: 'STOP', start: 'START', help: 'HELP', other: 'Other reply' }

// A keyword is the whole message (as carriers and Twilio read it): case,
// surrounding spaces and trailing punctuation don't matter. Twilio's own
// reading (OptOutType, sent when Advanced Opt-Out is on) wins when present.
export function classifySmsKeyword(body: string | null | undefined, providerOptOutType?: string | null): { keywordClass: SmsKeywordClass; keyword: string | null; needsReview: boolean } {
  const fromProvider = (providerOptOutType ?? '').toUpperCase()
  const word = (body ?? '')
    .normalize('NFKC')
    .trim()
    .replace(/^[\s"'“”‘’.!?,;:()*-]+|[\s"'“”‘’.!?,;:()*-]+$/g, '')
    .replace(/\s+/g, ' ')
    .toUpperCase()
  if (fromProvider === 'STOP' || (SMS_STOP_KEYWORDS as readonly string[]).includes(word)) return { keywordClass: 'stop', keyword: word || fromProvider, needsReview: false }
  if (fromProvider === 'START' || (SMS_START_KEYWORDS as readonly string[]).includes(word)) return { keywordClass: 'start', keyword: word || fromProvider, needsReview: false }
  if (fromProvider === 'HELP' || (SMS_HELP_KEYWORDS as readonly string[]).includes(word)) return { keywordClass: 'help', keyword: word || fromProvider, needsReview: false }
  // A free-text reply that may be asking to stop: recorded for staff
  // review (plan §8: "records free-text replies for staff review").
  const needsReview = /\b(stop|unsubscribe|cancel|quit|end|remove|opt[\s-]?out|revoke|do ?n[o']?t (text|message)|no more|wrong number)\b/i.test(body ?? '')
  return { keywordClass: 'other', keyword: null, needsReview }
}

// ----------------------------------------------------- encoding and segments

// GSM 03.38: the default alphabet (one septet each) and its extension table
// (an escape plus the character: two septets each).
const GSM_BASIC = new Set([...'@£$¥èéùìòÇ\nØø\rÅåΔ_ΦΓΛΩΠΨΣΘΞÆæßÉ !"#¤%&\'()*+,-./0123456789:;<=>?¡ABCDEFGHIJKLMNOPQRSTUVWXYZÄÖÑÜ§¿abcdefghijklmnopqrstuvwxyzäöñüà'])
const GSM_EXTENDED = new Set([...'^{}\\[~]|€\f'])

// Characters phones and word processors put in that force a whole text into
// UCS-2 (70 characters a segment instead of 160) for no visible gain. Hub
// replaces them itself, so the count and cost don't depend on whether
// Twilio's "smart encoding" is switched on.
const LOOKALIKES: [RegExp, string][] = [
  [/[‘’‚‛′]/g, "'"],
  [/[“”„‟″]/g, '"'],
  [/[‐‑‒–—―−]/g, '-'],
  [/…/g, '...'],
  [/[      ]/g, ' '],
  [/[​‌‍﻿]/g, ''],
]
export function smsNormalize(text: string): string {
  let out = text.replace(/\r\n?/g, '\n')
  for (const [re, to] of LOOKALIKES) out = out.replace(re, to)
  return out
}

export type SmsEncoding = 'gsm7' | 'ucs2'
export type SmsCount = { encoding: SmsEncoding; units: number; segments: number; perSegment: number; characters: number; nonGsm: string[] }

// How many segments a text costs. GSM-7: 160 septets in one segment, 153 in
// each part of a longer one (the rest is the concatenation header), and an
// extension character is never split across parts. UCS-2: 70 UTF-16 units,
// 67 a part, and a surrogate pair (an emoji) is never split.
export function smsSegments(text: string): SmsCount {
  const chars = [...text]
  const nonGsm = [...new Set(chars.filter((ch) => !GSM_BASIC.has(ch) && !GSM_EXTENDED.has(ch)))]
  if (nonGsm.length === 0) {
    const sizes = chars.map((ch) => (GSM_EXTENDED.has(ch) ? 2 : 1))
    const units = sizes.reduce((a, b) => a + b, 0)
    return { encoding: 'gsm7', units, segments: units <= 160 ? 1 : pack(sizes, 153), perSegment: units <= 160 ? 160 : 153, characters: chars.length, nonGsm }
  }
  const sizes = chars.map((ch) => ch.length)
  const units = sizes.reduce((a, b) => a + b, 0)
  return { encoding: 'ucs2', units, segments: units <= 70 ? 1 : pack(sizes, 67), perSegment: units <= 70 ? 70 : 67, characters: chars.length, nonGsm }
}

function pack(sizes: number[], limit: number): number {
  let segments = 1
  let used = 0
  for (const s of sizes) {
    if (used + s > limit) {
      segments += 1
      used = 0
    }
    used += s
  }
  return segments
}

// --------------------------------------------------------------- templates

// An SMS template is plain text with merge fields from a fixed list. Hub adds
// the parts the law and the carriers require and a template can't leave out
// (plan §8): the brand's name first, and on marketing "Reply STOP to opt out."
export const SMS_MERGE_FIELDS = {
  first_name: { label: 'First name', fallback: 'there' },
  full_name: { label: 'Full name', fallback: 'friend' },
  event_name: { label: 'Event name (set on the campaign)', fallback: 'our next event' },
  event_date: { label: 'Event date (set on the campaign)', fallback: 'soon' },
  mybre_url: { label: 'myBRE link', fallback: '' },
} as const
export type SmsMergeField = keyof typeof SMS_MERGE_FIELDS
export const SMS_MERGE_FIELD_KEYS = Object.keys(SMS_MERGE_FIELDS) as SmsMergeField[]
const MERGE = /\{\{\s*([a-z_]+)\s*(?:\|([^{}|]{0,40}))?\}\}/g

export const SMS_STOP_FOOTER = 'Reply STOP to opt out.'

export const SmsTemplateContentSchema = z.object({ text: z.string().trim().min(1).max(1000) })
export type SmsTemplateContent = z.infer<typeof SmsTemplateContentSchema>

// Public link shorteners: carriers filter texts that carry them (shared
// domains are used for spam), and 10DLC campaigns are refused for them.
export const SMS_SHORTENER_HOSTS = [
  'bit.ly', 'bitly.com', 'tinyurl.com', 't.co', 'goo.gl', 'ow.ly', 'is.gd', 'buff.ly', 'rebrand.ly', 'cutt.ly', 'shorturl.at', 'tiny.cc', 'rb.gy', 'bl.ink',
  't.ly', 's.id', 'v.gd', 'lnkd.in', 'shorte.st', 'adf.ly', 'qrco.de', 'tiny.one', 'shrtco.de', 'short.io', 'soo.gd', 'clck.ru', 'trib.al', 'dlvr.it', 'ift.tt',
] as const

export function smsLinks(text: string): string[] {
  return [...text.matchAll(/\b((?:https?:\/\/)?(?:[a-z0-9-]+\.)+[a-z]{2,}(?:\/[^\s]*)?)/gi)].map((m) => m[1]!)
}

export function smsTemplateIssues(content: SmsTemplateContent): string[] {
  const issues: string[] = []
  const text = content.text
  for (const m of text.matchAll(MERGE)) if (!(m[1]! in SMS_MERGE_FIELDS)) issues.push(`{{${m[1]}}} isn't a merge field a text can use. Use one of: ${SMS_MERGE_FIELD_KEYS.map((k) => `{{${k}}}`).join(', ')}`)
  if (/\{\{|\}\}/.test(text.replace(MERGE, ''))) issues.push('A {{ or }} isn’t part of a well-formed merge field')
  for (const link of smsLinks(text.replace(MERGE, ''))) {
    const host = link.replace(/^https?:\/\//i, '').split('/')[0]!.toLowerCase()
    if ((SMS_SHORTENER_HOSTS as readonly string[]).some((h) => host === h || host.endsWith(`.${h}`))) issues.push(`${host} is a public link shortener; carriers block texts with them. Use the full link on our own domain.`)
    else if (/^http:\/\//i.test(link)) issues.push(`Use https:// for ${link}`)
  }
  if (/reply\s+stop|text\s+stop|\bstop\s+to\s+(opt|end|cancel|unsub)/i.test(text)) issues.push('Leave out the opt-out line; “Reply STOP to opt out.” is added to every marketing text')
  return [...new Set(issues)]
}

export type SmsMergeValues = { first_name: string | null; full_name: string | null; event_name: string | null; event_date: string | null; mybre_url: string | null }
export type RenderedSms = { text: string; count: SmsCount }

export class SmsRenderError extends Error {
  constructor(message: string) {
    super(message)
    this.name = 'SmsRenderError'
  }
}

// The text exactly as it goes: "<Brand>: <template, merged>" and, for
// marketing, the opt-out line. Normalized (no smart quotes), and refused if
// it is over the segment limit.
export function renderSms(input: { content: SmsTemplateContent; brandName: string; marketing: boolean; merge: SmsMergeValues; test?: boolean; maxSegments?: number }): RenderedSms {
  const issues = smsTemplateIssues(input.content)
  if (issues.length) throw new SmsRenderError(issues[0]!)
  const merged = input.content.text.replace(MERGE, (_all, field: string, fallback?: string) => {
    const v = input.merge[field as SmsMergeField]
    if (v && v.trim()) return v.trim()
    if (fallback !== undefined) return fallback.trim()
    return SMS_MERGE_FIELDS[field as SmsMergeField]?.fallback ?? ''
  })
  const brand = input.brandName.trim()
  if (!brand) throw new SmsRenderError('The brand needs a name')
  let body = merged.trim()
  // The template may start with the brand's name already; it isn't doubled.
  if (!body.toLowerCase().startsWith(`${brand.toLowerCase()}:`)) body = `${brand}: ${body}`
  if (input.test) body = `[TEST] ${body}`
  if (input.marketing) body = `${body}\n${SMS_STOP_FOOTER}`
  const text = smsNormalize(body).replace(/[ \t]+\n/g, '\n').replace(/\n{3,}/g, '\n\n')
  const count = smsSegments(text)
  if (input.maxSegments && count.segments > input.maxSegments) throw new SmsRenderError(`This text is ${count.segments} segments; the limit is ${input.maxSegments}`)
  return { text, count }
}

// The SMS marketing box on a Hub form: its label IS the disclosure the
// person agrees to, so it must carry everything the TCPA and the carriers
// ask for (plan §8). Counsel reviews the wording.
export function smsDisclosureIssues(text: string): string[] {
  const issues: string[] = []
  if (!/\bSTOP\b/.test(text)) issues.push('say how to stop (Reply STOP)')
  if (!/\bHELP\b/.test(text)) issues.push('say how to get help (HELP)')
  if (!/rates may apply/i.test(text)) issues.push('say message and data rates may apply')
  if (!/not a condition|isn[’']?t a condition/i.test(text)) issues.push('say consent is not a condition of purchase')
  if (!/recurring|automated/i.test(text)) issues.push('say these are recurring automated texts')
  if (!/marketing|offers|news|announcements/i.test(text)) issues.push('say what the texts are about')
  return issues
}

// ------------------------------------------------------------- quiet hours

// When texts may go out, in the recipient's local time: [start, end) in
// minutes after midnight, on the allowed days (Sunday = 0).
export type SmsWindow = { startMinute: number; endMinute: number; days: string }

function localParts(at: Date, timeZone: string) {
  const parts = new Intl.DateTimeFormat('en-US', { timeZone, hourCycle: 'h23', year: 'numeric', month: 'numeric', day: 'numeric', hour: 'numeric', minute: 'numeric', second: 'numeric' }).formatToParts(at)
  const get = (t: string) => Number(parts.find((p) => p.type === t)?.value ?? '0')
  const y = get('year')
  const m = get('month')
  const d = get('day')
  const hour = get('hour') % 24
  const minute = get('minute')
  return { y, m, d, hour, minute, second: get('second'), weekday: new Date(Date.UTC(y, m - 1, d)).getUTCDay() }
}

export function isTimeZone(value: string): boolean {
  try {
    new Intl.DateTimeFormat('en-US', { timeZone: value }).format(new Date(0))
    return true
  } catch {
    return false
  }
}

// The instant a local wall-clock time happens in a zone (the earlier one
// when the clock falls back; the hour after when it springs forward).
export function zonedTimeToUtc(y: number, m: number, d: number, hour: number, minute: number, timeZone: string): Date {
  const guess = Date.UTC(y, m - 1, d, hour, minute)
  const offsetAt = (t: number) => {
    const p = localParts(new Date(t), timeZone)
    return Date.UTC(p.y, p.m - 1, p.d, p.hour, p.minute, p.second) - Math.floor(t / 1000) * 1000
  }
  let t = guess - offsetAt(guess)
  t = guess - offsetAt(t)
  return new Date(t)
}

// Whether a text may go now, and if not, the next moment it may.
export function smsWindowCheck(now: Date, timeZone: string, w: SmsWindow): { allowed: boolean; nextAllowedAt: string } {
  const p = localParts(now, timeZone)
  const minute = p.hour * 60 + p.minute
  const dayOk = (wd: number) => w.days[wd] === '1'
  if (dayOk(p.weekday) && minute >= w.startMinute && minute < w.endMinute) return { allowed: true, nextAllowedAt: now.toISOString() }
  for (let add = 0; add <= 7; add += 1) {
    const date = new Date(Date.UTC(p.y, p.m - 1, p.d + add))
    if (!dayOk(date.getUTCDay())) continue
    if (add === 0 && minute >= w.startMinute) continue
    const at = zonedTimeToUtc(date.getUTCFullYear(), date.getUTCMonth() + 1, date.getUTCDate(), Math.floor(w.startMinute / 60), w.startMinute % 60, timeZone)
    if (at.getTime() > now.getTime()) return { allowed: false, nextAllowedAt: at.toISOString() }
  }
  // No allowed day at all: a week out, and checked again then.
  return { allowed: false, nextAllowedAt: new Date(now.getTime() + 7 * 86_400_000).toISOString() }
}

// The calendar month a moment falls in, in the zone, as [start, end) instants:
// the budget's month.
export function monthBounds(now: Date, timeZone: string): { start: string; end: string; label: string } {
  const p = localParts(now, timeZone)
  const start = zonedTimeToUtc(p.y, p.m, 1, 0, 0, timeZone)
  const next = p.m === 12 ? { y: p.y + 1, m: 1 } : { y: p.y, m: p.m + 1 }
  const end = zonedTimeToUtc(next.y, next.m, 1, 0, 0, timeZone)
  return { start: start.toISOString(), end: end.toISOString(), label: `${p.y}-${String(p.m).padStart(2, '0')}` }
}

export const minuteLabel = (m: number) => {
  const h = Math.floor(m / 60) % 24
  const mm = m % 60
  const suffix = h < 12 ? 'a.m.' : 'p.m.'
  const h12 = h % 12 === 0 ? 12 : h % 12
  return m === 1440 ? 'midnight' : `${h12}${mm ? `:${String(mm).padStart(2, '0')}` : ''} ${suffix}`
}
export const DAY_LETTERS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'] as const
export function windowLabel(w: SmsWindow): string {
  const days = DAY_LETTERS.filter((_, i) => w.days[i] === '1')
  const dayText = days.length === 7 ? 'every day' : days.length === 0 ? 'no day' : days.join(', ')
  return `${minuteLabel(w.startMinute)} to ${minuteLabel(w.endMinute)}, ${dayText}`
}

// ------------------------------------------------------------------ money

// A segment's price is in millionths of a dollar; budgets are cents.
export const microsToCents = (micros: number) => micros / 10_000
export const formatMicros = (micros: number) => {
  const dollars = micros / 1_000_000
  return dollars < 1 && dollars > 0 ? `$${dollars.toFixed(dollars < 0.1 ? 4 : 2)}` : `$${dollars.toFixed(2)}`
}
// A US number as people write it; anything else as stored.
export const formatPhone = (e164: string) => (/^\+1\d{10}$/.test(e164) ? `(${e164.slice(2, 5)}) ${e164.slice(5, 8)}-${e164.slice(8)}` : e164)
export const formatCents = (cents: number) => `$${(cents / 100).toFixed(2)}`

// ---------------------------------------------------------------- settings

export type SmsService = {
  key: string
  name: string
  sendClass: 'marketing' | 'operational'
  messagingServiceSid: string | null
  phoneNumber: string | null
  registeredAt: string | null
  active: boolean
  updatedAt: string
}

export type SmsSettings = {
  smsProvider: 'twilio' | 'none'
  segmentPriceMicros: number
  monthlyBudgetCents: number | null
  budgetHardStop: boolean
  timeZone: string
  marketingWindow: SmsWindow
  operationalWindow: SmsWindow
  frequencyCapCount: number
  frequencyCapHours: number
  approvalOver: number
  maxSegments: number
  keywordReplies: 'twilio' | 'hub'
  helpReply: string
  stopReply: string
  startReply: string
  optinText: string
  updatedAt: string | null
  updatedByName: string | null
}

const Window = z
  .object({ startMinute: z.number().int().min(0).max(1439), endMinute: z.number().int().min(1).max(1440), days: z.string().regex(/^[01]{7}$/) })
  .refine((w) => w.endMinute > w.startMinute, 'The window must end after it starts')
const ReplyText = z.string().trim().min(10).max(320)

export const SmsSettingsSchema = z.object({
  smsProvider: z.enum(['twilio', 'none']),
  segmentPriceMicros: z.number().int().min(0).max(1_000_000),
  monthlyBudgetCents: z.number().int().min(0).max(100_000_000).nullable(),
  budgetHardStop: z.boolean(),
  timeZone: z.string().trim().min(3).max(60).refine(isTimeZone, 'Not a time zone'),
  // Never later than 9 p.m. or earlier than 8 a.m. (the federal limit for
  // telephone solicitations, 47 CFR 64.1200(c)(1)).
  marketingWindow: Window.refine((w) => w.startMinute >= 480 && w.endMinute <= 1260, 'Marketing texts stay between 8 a.m. and 9 p.m.'),
  operationalWindow: Window,
  frequencyCapCount: z.number().int().min(1).max(50),
  frequencyCapHours: z.number().int().min(1).max(744),
  approvalOver: z.number().int().min(0).max(100_000),
  maxSegments: z.number().int().min(1).max(10),
  keywordReplies: z.enum(['twilio', 'hub']),
  helpReply: ReplyText.refine((t) => /\bSTOP\b/.test(t), 'The HELP answer must say how to stop'),
  stopReply: ReplyText,
  startReply: ReplyText.refine((t) => /\bSTOP\b/.test(t), 'The START answer must say how to stop'),
  optinText: ReplyText.refine((t) => /\bSTOP\b/.test(t) && /\bHELP\b/.test(t), 'The sign-up text must say how to stop and get help'),
})

export const SmsServiceBodySchema = z.object({
  name: z.string().trim().min(1).max(100),
  messagingServiceSid: z.string().trim().regex(/^MG[0-9a-f]{32}$/, 'A Messaging Service SID is MG and 32 hex characters').nullable(),
  phoneNumber: z.string().trim().min(8).max(20).nullable(),
  registered: z.boolean(),
})

// Where texting stands here, for every screen: live only in production with
// Twilio chosen, its credentials set and the service's SID filled in.
export type SmsProviderStatus = {
  mode: 'live' | 'simulated' | 'not_set_up'
  reason: string
  services: { key: string; sendClass: 'marketing' | 'operational'; mode: 'live' | 'simulated' | 'not_set_up'; reason: string }[]
}

// ------------------------------------------------------- Twilio signatures

// X-Twilio-Signature (twilio.com/docs/usage/security, read 2026-10-09): the
// full URL Twilio called (scheme to query string), then every POST
// parameter's name and value appended with no separators, sorted by name;
// HMAC-SHA1 under the account's Auth Token; base64.
export function twilioSignedString(url: string, params: Iterable<[string, string]>): string {
  const sorted = [...params].sort(([a, av], [b, bv]) => (a < b ? -1 : a > b ? 1 : av < bv ? -1 : av > bv ? 1 : 0))
  return url + sorted.map(([k, v]) => `${k}${v}`).join('')
}

export async function twilioSignature(authToken: string, url: string, params: Iterable<[string, string]>): Promise<string> {
  const enc = new TextEncoder()
  const key = await crypto.subtle.importKey('raw', enc.encode(authToken), { name: 'HMAC', hash: 'SHA-1' }, false, ['sign'])
  const mac = new Uint8Array(await crypto.subtle.sign('HMAC', key, enc.encode(twilioSignedString(url, params))))
  let binary = ''
  for (const b of mac) binary += String.fromCharCode(b)
  return btoa(binary)
}

export async function verifyTwilioSignature(authToken: string, url: string, params: Iterable<[string, string]>, header: string | null): Promise<boolean> {
  if (!authToken || !header) return false
  const expected = await twilioSignature(authToken, url, params)
  if (expected.length !== header.length) return false
  let diff = 0
  for (let i = 0; i < expected.length; i += 1) diff |= expected.charCodeAt(i) ^ header.charCodeAt(i)
  return diff === 0
}

// ------------------------------------------------------- the outbox's texts

// One rendered text, claimed, for the web Worker to send.
export type SmsEnvelope = {
  messageId: string
  claimId: string
  sendClass: 'marketing' | 'operational'
  to: string
  body: string
  serviceKey: string
  // The Messaging Service's SID, when set (never a credential).
  messagingServiceSid: string | null
  brandKey: string
  campaignId: string | null
  test: boolean
}

// An inbound text, normalized (from Twilio's webhook, or simulated through
// the same path).
export type SmsInbound = {
  provider: 'twilio' | 'simulated'
  providerMessageId: string
  from: string
  to: string | null
  messagingServiceSid: string | null
  // For simulated replies: which of Hub's services it was "sent" to.
  serviceKey: string | null
  body: string | null
  optOutType: string | null
  receivedAt: string
  payloadHash: string | null
}

export const SmsInboundSchema = z.object({
  provider: z.enum(['twilio', 'simulated']),
  providerMessageId: z.string().min(1).max(100),
  from: z.string().min(3).max(40),
  to: z.string().max(40).nullable(),
  messagingServiceSid: z.string().max(40).nullable(),
  serviceKey: z.string().max(40).nullable(),
  body: z.string().max(1600).nullable(),
  optOutType: z.string().max(20).nullable(),
  receivedAt: z.string().max(60),
  payloadHash: z.string().max(100).nullable(),
})

export type SmsInboundResult = { duplicate: boolean; inboundId: string | null; keywordClass: SmsKeywordClass; reply: string | null; stopsWritten: number; releasesWritten: number; withdrawals: number }

// Twilio status callbacks (MessageStatus) and the error codes Hub acts on.
// From Twilio's docs as remembered, not re-read here (unverified):
//   21610  the number replied STOP to this sender: a STOP for that service
//   21211  not a valid phone number; 21614 not a mobile number;
//   30006  landline or unreachable carrier: an invalid_number suppression
export const TWILIO_STOPPED_CODES = ['21610'] as const
export const TWILIO_INVALID_NUMBER_CODES = ['21211', '21614', '30006'] as const
